import {
  Box,
  Divider,
  List,
  ListItemButton,
  ListProps,
  Stack,
} from '@mui/material';
import ListItemText from '@mui/material/ListItemText';
import * as React from 'react';
import { useEffect } from 'react';
import { EventCategories } from '@/constant/event';
import Collapse from '@mui/material/Collapse';
import { GrayArrowDown, GrayArrowUp } from '@/theme/overrides/CustomIcons';
import { SOCIAL_AX_LOUNGE_GROUP_ID } from '@/constant/url';

type P = {
  data: SideCategoryItem[];
  value: string;
  onCategoryChange: (v: SideCategoryItem) => void;
} & ListProps;

const availableEvents = Array.from(EventCategories.values()).filter(
  (event) => event.isShow && event.data,
);
export const EventCategoryIds = availableEvents.map(
  (event) => event.data.value,
);
export const isShowEventCategory = availableEvents.length > 0;

export function SideCategory({ data, value, onCategoryChange, ...other }: P) {
  const [selectItem, setSelectItem] = React.useState<string>(value);

  useEffect(() => {
    setSelectItem(value);
  }, [value]);

  const select = (v: SideCategoryItem) => {
    setSelectItem(v.value);
    onCategoryChange(v);
  };

  return (
    <List sx={{ width: '100%', padding: 0 }} component="nav" {...other}>
      <Stack spacing={1.5}>
        {data.map((item) => (
          <SideCategoryItem
            key={'categoryIem_' + item.value}
            data={item}
            selectItem={selectItem}
            onClick={select}
          />
        ))}

        {isShowEventCategory && (
          <>
            <Divider />
            {availableEvents.map((item) => (
              <SideCategoryItem
                key={'categoryIem_' + item.data.value}
                data={item.data}
                selectItem={selectItem}
                onClick={select}
              />
            ))}
          </>
        )}
      </Stack>
    </List>
  );
}

export type SideCategoryItem = {
  title: string;
  value: string;
  count?: number;
  child?: SideCategoryItem[];
};

type SideCategoryItemP = {
  data: SideCategoryItem;
  selectItem: string;
  onClick: (v: SideCategoryItem) => void;
};

function SideCategoryItem({ data, selectItem, onClick }: SideCategoryItemP) {
  const [open, setOpen] = React.useState(true);
  const [isSelected, setSelected] = React.useState(selectItem === data.value);

  useEffect(() => {
    setSelected(selectItem === data.value ?? false);
  }, [selectItem]);

  const handleClick = () => {
    setOpen(!open);
    onClick(data);
  };

  return (
    <Box>
      <ListItemButton
        onClick={handleClick}
        sx={{
          p: '6px 10px',
          background: isSelected ? 'rgba(0, 102, 204, 0.10)' : 'transparent',
          borderRadius: '4px',
          color: isSelected ? '#06C' : 'black',
          '&:hover': {
            background: isSelected ? 'rgba(0, 102, 204, 0.10)' : 'transparent',
          },
        }}
      >
        <ListItemText
          sx={{ m: 0 }}
          primaryTypographyProps={{
            sx: {
              fontWeight: isSelected ? 500 : 400,
              lineHeight: '20px',
              fontSize: '16px',
            },
          }}
          primary={
            <Box>
              {data.title}
              {data.count && (
                <span
                  style={{
                    display: 'inline-block',
                    marginLeft: '8px',
                    fontWeight: 'bold',
                    fontSize: '16px',
                  }}
                >
                  {data.count}
                </span>
              )}
            </Box>
          }
        />
        {data.child && data.value === SOCIAL_AX_LOUNGE_GROUP_ID && (open ? <GrayArrowUp /> : <GrayArrowDown />)}
      </ListItemButton>
      {data.child && data.value === SOCIAL_AX_LOUNGE_GROUP_ID && (
        <Collapse in={open} timeout="auto" unmountOnExit sx={{ mt: 1 }}>
          <List component="div" disablePadding sx={{ pl: 4 }}>
            <Stack spacing={1}>
              {data.child.map((item) => (
                <SideCategoryItem
                  key={item.value}
                  data={item}
                  selectItem={selectItem}
                  onClick={() => onClick(item)}
                />
              ))}
            </Stack>
          </List>
        </Collapse>
      )}
    </Box>
  );
}
